import { Expose, Exclude } from 'class-transformer';
import { DocumentSnapshot } from 'firebase/firestore';
import { _FirebaseDocument } from './Models';

/**
 * A published version of the terms of use (Firestore: terms_versions/{version}),
 * MYAC-824. Carries both the full body (markdown, per locale) and the version
 * metadata, so the text the user reads and the version their consent is recorded
 * against come from this one doc — no drift with a deployed bundle. Publicly
 * readable; the in-force version is selected by system/public_config.terms.
 */
@Exclude()
export class TermsVersion extends _FirebaseDocument {
    @Expose()
    public version?: string;

    @Expose()
    public sequence?: number;

    @Expose()
    public material?: boolean;

    /** Full terms body as markdown, keyed by base locale, e.g. { en, fr }. */
    @Expose()
    public body?: Record<string, string>;

    /** sha256 of body[locale] per locale — echoed onto the consent record. */
    @Expose()
    public content_hash?: Record<string, string>;

    collection(): string {
        return 'terms_versions';
    }

    /**
     * Resolve which body/hash key to use for a UI locale: exact base language
     * (e.g. `en-GB` → `en`), then English, then any available locale.
     */
    private resolveKey(locale: string): string | undefined {
        if (!this.body) return undefined;
        const base = (locale || '').split('-')[0].toLowerCase();
        if (this.body[base]) return base;
        if (this.body['en']) return 'en';
        const keys = Object.keys(this.body);
        return keys.length ? keys[0] : undefined;
    }

    /** Body markdown for a locale, or '' when no body is present. */
    bodyFor(locale: string): string {
        const key = this.resolveKey(locale);
        return (key && this.body?.[key]) || '';
    }

    /** Content hash of the exact body shown for a locale, or null. */
    contentHashFor(locale: string): string | null {
        const key = this.resolveKey(locale);
        return (key && this.content_hash?.[key]) || null;
    }

    static fromFirebase(o: DocumentSnapshot): TermsVersion | null {
        return _FirebaseDocument._fromFirebase(TermsVersion, o);
    }
}
